import { Edge, Node } from '@xyflow/react';

import { ParameterManager } from '../nodes/parameters/ParameterManager';
import { NodeConfig } from '../types/node-config';
import { NodeValidationError, NodeValidationResult } from '../types/node-validation';

/**
 * Validate a single node's parameters against its configuration.
 */
export function validateNode(
  node: Node,
  getNodeConfig: (type: string) => NodeConfig | undefined,
  parameterManager: ParameterManager
): NodeValidationResult {
  const nodeType = getNodeConfig(node.type || '');

  if (!nodeType) {
    return {
      isValid: false,
      errors: [
        {
          nodeId: node.id,
          message: `Unknown node type: ${node.type}`,
          category: 'type',
        },
      ],
    };
  }

  const properties = nodeType.description.properties || [];
  const parameters = (node.data as Record<string, unknown>) || {};

  const validationResult = parameterManager.validateParameters(
    parameters,
    properties,
    node.type || ''
  );

  const errors: NodeValidationError[] = validationResult.errors.map(error => ({
    nodeId: node.id,
    parameterName: error.parameterName,
    message: error.message,
    category: 'parameter',
  }));

  return {
    isValid: validationResult.isValid,
    errors,
  };
}

/**
 * Validate multiple nodes and return combined results.
 */
function validateNodes(
  nodes: Node[],
  getNodeConfig: (type: string) => NodeConfig | undefined,
  parameterManager: ParameterManager
): NodeValidationResult {
  const allErrors: NodeValidationError[] = [];
  let isValid = true;

  for (const node of nodes) {
    const result = validateNode(node, getNodeConfig, parameterManager);
    if (!result.isValid) {
      isValid = false;
      allErrors.push(...result.errors);
    }
  }

  return {
    isValid,
    errors: allErrors,
  };
}

/**
 * Check if a node has any connections (incoming or outgoing).
 */
export function hasAnyConnection(nodeId: string, edges: Edge[]): boolean {
  return edges.some(edge => edge.source === nodeId || edge.target === nodeId);
}

/**
 * Get nodes that have no connections.
 */
export function getIsolatedNodes(nodes: Node[], edges: Edge[]): Node[] {
  return nodes.filter(node => !hasAnyConnection(node.id, edges));
}

/**
 * Validate node connectivity - only applies when there are multiple nodes.
 */
export function validateNodeConnectivity(nodes: Node[], edges: Edge[]): NodeValidationResult {
  const errors: NodeValidationError[] = [];

  // Group nodes by their parent (root level has no parent, ForLoop children have parent)
  const nodeGroups = new Map<string | null, Node[]>();

  nodes.forEach(node => {
    const parentId = node.parentId || null;
    if (!nodeGroups.has(parentId)) {
      nodeGroups.set(parentId, []);
    }
    const group = nodeGroups.get(parentId);
    if (group) {
      group.push(node);
    }
  });

  // Validate each group separately
  nodeGroups.forEach(groupNodes => {
    // Only validate if there are multiple nodes in this group
    if (groupNodes.length > 1) {
      const isolatedNodes = getIsolatedNodes(groupNodes, edges);

      isolatedNodes.forEach(node => {
        errors.push({
          nodeId: node.id,
          message: 'Node is not connected to any other node',
          category: 'connectivity',
        });
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validate connection rules based on node configuration.
 * Validates minConnections, output filters, and per-type maxConnections from config.
 */
export function validateConnectionConfigRules(
  nodes: Node[],
  edges: Edge[],
  getNodeConfig: (type: string) => NodeConfig | undefined
): NodeValidationResult {
  const errors: NodeValidationError[] = [];

  for (const node of nodes) {
    const nodeConfig = getNodeConfig(node.type || '');
    if (!nodeConfig) continue;

    const outputConfig = nodeConfig.description.output;
    if (!outputConfig) continue;

    const outgoingEdges = edges.filter(e => e.source === node.id);

    // Check minConnections
    if (outputConfig.minConnections !== undefined) {
      if (outgoingEdges.length < outputConfig.minConnections) {
        errors.push({
          nodeId: node.id,
          message: `${nodeConfig.description.displayName} requires at least ${outputConfig.minConnections} outgoing connection(s)`,
          category: 'connectivity',
        });
      }
    }

    // Check output filter (allowed target types)
    if (outputConfig.filter?.allowedNodeTypes) {
      for (const edge of outgoingEdges) {
        const targetNode = nodes.find(n => n.id === edge.target);
        if (!targetNode) continue;

        const isAllowed = outputConfig.filter.allowedNodeTypes.some(
          allowed => allowed.type === targetNode.type
        );

        if (!isAllowed) {
          const targetConfig = getNodeConfig(targetNode.type || '');
          errors.push({
            nodeId: node.id,
            message: `${nodeConfig.description.displayName} cannot connect to ${targetConfig?.description.displayName || targetNode.type}`,
            category: 'connectivity',
          });
        }
      }

      // Check per-type maxConnections in output filter
      for (const allowedType of outputConfig.filter.allowedNodeTypes) {
        const connectionsToType = outgoingEdges.filter(edge => {
          const target = nodes.find(n => n.id === edge.target);
          return target?.type === allowedType.type;
        }).length;

        if (connectionsToType > allowedType.maxConnections) {
          errors.push({
            nodeId: node.id,
            message: `Maximum ${allowedType.maxConnections} connection(s) to ${allowedType.type} exceeded`,
            category: 'connectivity',
          });
        }
      }
    }
  }

  return { isValid: errors.length === 0, errors };
}

/**
 * Validate both node parameters and connectivity.
 */
export function validateNodesWithConnectivity(
  nodes: Node[],
  edges: Edge[],
  getNodeConfig: (type: string) => NodeConfig | undefined,
  parameterManager: ParameterManager
): NodeValidationResult {
  const parameterValidation = validateNodes(nodes, getNodeConfig, parameterManager);
  const connectivityValidation = validateNodeConnectivity(nodes, edges);
  const configRulesValidation = validateConnectionConfigRules(nodes, edges, getNodeConfig);

  return {
    isValid:
      parameterValidation.isValid &&
      connectivityValidation.isValid &&
      configRulesValidation.isValid,
    errors: [
      ...parameterValidation.errors,
      ...connectivityValidation.errors,
      ...configRulesValidation.errors,
    ],
  };
}
